import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import styled from 'styled-components';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import {
  customizationBelongsToInboxFilter,
  customizationBuyerName,
  formatCustomizationMoney,
} from '../../api/customizationsAPI';
import { getOfferingVertical } from './offeringCustomizationVertical';

const Page = styled.main`max-width: 980px; margin: 0 auto; padding: 2rem 1rem 5rem;`;
const Title = styled.h1`color: var(--text-primary); font-size: 2rem; margin: 0 0 1.25rem;`;
const Filters = styled.div`display: flex; flex-wrap: wrap; gap: 0.5rem; margin-bottom: 1.5rem;`;
const Chip = styled.button`
  background: ${(p) => (p.$active ? '#0f3d36' : 'var(--bg-primary, #fff)')};
  border: 1px solid ${(p) => (p.$active ? '#0f3d36' : 'var(--border-color, #d1d5db)')};
  border-radius: 999px; color: ${(p) => (p.$active ? '#fff' : 'var(--text-primary)')};
  cursor: pointer; font-weight: 700; padding: 0.55rem 1rem;
`;
const List = styled.div`display: grid; gap: 1rem;`;
const Card = styled(Link)`
  background: var(--bg-primary, #fff); border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 0.8rem; color: inherit; padding: 1.25rem; text-decoration: none;
`;
const Header = styled.div`align-items: flex-start; display: flex; gap: 1rem; justify-content: space-between;`;
const Name = styled.h2`font-size: 1.05rem; margin: 0;`;
const DateText = styled.div`color: var(--text-secondary); font-size: 0.8rem; margin-top: 0.25rem;`;
const Badge = styled.span`
  background: #e7f4ef; border-radius: 999px; color: #0f3d36; font-size: 0.75rem;
  font-weight: 800; padding: 0.4rem 0.65rem;
`;
const Meta = styled.div`color: var(--text-secondary); font-size: 0.875rem; margin-top: 0.35rem;`;
const LinkText = styled.div`color: #0f3d36; font-weight: 800; margin-top: 0.9rem;`;
const State = styled.div`background: var(--bg-primary, #fff); border-radius: 0.75rem; padding: 3rem 1.5rem; text-align: center;`;
const Retry = styled.button`
  background: #0f3d36; border: 0; border-radius: 0.5rem; color: #fff;
  cursor: pointer; font-weight: 700; margin-top: 1rem; padding: 0.7rem 1.1rem;
`;
const Tools = styled.div`display: flex; flex-wrap: wrap; gap: 0.75rem; margin-bottom: 1.25rem;`;
const ToolLink = styled(Link)`color: #0f3d36; font-weight: 800;`;

const filters = [
  { id: 'needs_response', label: 'Needs response' },
  { id: 'active', label: 'Active' },
  { id: 'closed', label: 'Closed' },
];
const statusCopy = {
  sent: 'New request',
  quoted: 'Quote sent',
  accepted: 'Quote accepted',
  converted: 'Ordered',
  cancelled: 'Cancelled by buyer',
  expired: 'Expired',
};

export function OfferingCustomizationInboxPage({ verticalKey = 'boutique' }) {
  const vertical = getOfferingVertical(verticalKey);
  const [filter, setFilter] = useState('needs_response');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await vertical.getInbox();
      setRequests(data.results);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [vertical]);

  useEffect(() => { load(); }, [load]);
  const visible = useMemo(
    () => requests.filter((request) => customizationBelongsToInboxFilter(request.state, filter)),
    [filter, requests],
  );

  if (loading) return <LoadingSpinner text="Loading custom orders…" />;

  return (
    <Page>
      <Title>Custom order requests</Title>
      <Tools>
        <ToolLink to={vertical.ownerSettingsPath}>Customization settings</ToolLink>
      </Tools>
      <Filters>
        {filters.map((item) => (
          <Chip key={item.id} type="button" $active={filter === item.id} onClick={() => setFilter(item.id)}>
            {item.label}
          </Chip>
        ))}
      </Filters>
      {error ? (
        <State>
          <h2>Something went wrong.</h2>
          <Retry type="button" onClick={load}>Try again</Retry>
        </State>
      ) : !visible.length ? (
        <State>
          <h2>No requests in this view.</h2>
          <p>New custom order requests will appear here.</p>
        </State>
      ) : (
        <List>
          {visible.map((request) => (
            <Card key={request.id} to={vertical.ownerDetailPath(request.id)}>
              <Header>
                <div>
                  <Name>{customizationBuyerName(request)}</Name>
                  <DateText>
                    {request.sent_at ? new Date(request.sent_at).toLocaleString() : '—'}
                  </DateText>
                </div>
                <Badge>{statusCopy[request.state] || request.state}</Badge>
              </Header>
              <Meta>{request.offering?.title || 'Custom order'}</Meta>
              <Meta>
                Budget {formatCustomizationMoney(request.budget_min_cents)}
                {' – '}
                {formatCustomizationMoney(request.budget_max_cents)}
              </Meta>
              <LinkText>Open request →</LinkText>
            </Card>
          ))}
        </List>
      )}
    </Page>
  );
}

const BoutiqueCustomizationInboxPage = () => <OfferingCustomizationInboxPage verticalKey="boutique" />;
export const DesignerCustomizationInboxPage = () => <OfferingCustomizationInboxPage verticalKey="designer" />;
export default BoutiqueCustomizationInboxPage;
